import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import AnnotationCheck from "@redotech/redo-icons/annotation-check.svg";
import CheckSvg from "@redotech/redo-icons/check_filled.svg";
import ClockRewindIcon from "@redotech/redo-icons/clock-rewind_filled.svg";
import DotsHorizontalSvg from "@redotech/redo-icons/dots-horizontal.svg";
import Edit04Icon from "@redotech/redo-icons/edit-04.svg";
import RefreshCCW01Svg from "@redotech/redo-icons/refresh-ccw-01_filled.svg";
import StatusSvg from "@redotech/redo-icons/status.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  ConversationPlatform,
  ConversationStatus,
  ExpandedConversation,
  MinimalConversation,
  getConversationStatus,
} from "@redotech/redo-model/conversation";
import {
  FilterGroupFilterOption,
  FiltersStatus,
} from "@redotech/redo-model/conversation-filters/conversation-filters";
import { SPAM_TAG } from "@redotech/redo-model/conversation-tags/conversation-tag-record";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { SortDirection, TableSort } from "@redotech/redo-model/tables/table";
import { GetUser as User } from "@redotech/redo-model/user";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCommandMenu } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { CardClickHandler, CardList } from "@redotech/redo-web/card-list";
import { Flex } from "@redotech/redo-web/flex";
import ArchiveIcon from "@redotech/redo-web/icon-old/archive.svg";
import EnvelopeIcon from "@redotech/redo-web/icon-old/mail.svg";
import MergeIcon from "@redotech/redo-web/icon-old/merge.svg";
import SnoozeClockIcon from "@redotech/redo-web/icon-old/snooze-clock.svg";
import SpamIcon from "@redotech/redo-web/icon-old/spam.svg";
import UndoArrow from "@redotech/redo-web/icon-old/undo-arrow.svg";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { sinkPromise } from "@redotech/util/promise";
import { browserIsMac } from "@redotech/web-util/browser";
import classNames from "classnames";
import {
  KeyboardEvent,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { ConversationContext } from "./action-panel/conversation-context";
import {
  getMinimalConversations,
  updateConversation,
} from "./client/conversations";
import { ActiveConversationContext } from "./context/active-conversation-context";
import { ConversationCloseValidationContext } from "./context/conversation-close-validation-context";
import { UpdateConversationStateContext } from "./context/update-conversations-context";
import { ArchiveTicketModal } from "./conversation-actions/archive-ticket-modal";
import { ChangeSubjectModal } from "./conversation-actions/change-subject-modal";
import {
  CloseTicketModal,
  handleCloseTickets,
} from "./conversation-actions/close-ticket-modal";
import { MarkSpamModal } from "./conversation-actions/mark-spam-modal";
import { MergeModal } from "./conversation-actions/merge-modal";
import { ReopenTicketModal } from "./conversation-actions/reopen-ticket-modal";
import { SnoozeModal } from "./conversation-actions/snooze-modal";
import { UnmarkSpamModal } from "./conversation-actions/unmark-spam-modal";
import { ConversationContent } from "./conversation-content";
import * as conversationHeaderCss from "./conversation-header.module.css";
import { ConversationInfoCardRender } from "./conversation-info-card";
import { ConversationSendStateContextProvider } from "./conversation-send-states";
import {
  AdvancedFiltersContext,
  FinalizedFiltersContext,
  SetUniqueFiltersContext,
  UniqueFiltersContext,
} from "./conversations-table-filters/filters-context";
import {
  markConversationAsRead,
  markConversationAsUnread,
} from "./conversations/mark-conversation-read-manager";
import { GenericConversationFetcher } from "./generic-conversation-fetcher";
import { useConversationCloseValidation } from "./hooks/use-conversation-close-validation";
import { useMergeSuggestions } from "./hooks/use-merge-suggestions";
import { MergeSuggestionFlow } from "./merge-suggestion/merge-suggestion-flow";
import * as singleConversationViewCss from "./single-conversation-view.module.css";

export const SingleConversationView = memo(function SingleConversationView({
  fullConversation,
  fullConversationLoading,
  activeConversation,
  setActiveConversation,
  setFullConversation,
  fetcher,
  blockRefresh,
}: {
  fullConversation: ExpandedConversation | undefined;
  fullConversationLoading: boolean;
  activeConversation: MinimalConversation;
  setActiveConversation: (
    conversation: MinimalConversation | undefined,
  ) => void;
  setFullConversation: (conversation: ExpandedConversation | undefined) => void;
  fetcher: GenericConversationFetcher<MinimalConversation>;
  blockRefresh: boolean;
}) {
  const navigate = useNavigate();
  const client = useRequiredContext(RedoMerchantClientContext);
  const user = useRequiredContext(UserContext);
  const team = useRequiredContext(TeamContext);
  const filters = useContext(UniqueFiltersContext);
  const advancedFilters = useContext(AdvancedFiltersContext);
  const setFilters = useContext(SetUniqueFiltersContext);
  const finalizedFilters = useContext(FinalizedFiltersContext);
  const { removeConversation } = useRequiredContext(
    UpdateConversationStateContext,
  );

  const { cardListRef, waitForRefresh } = useRequiredContext(
    UpdateConversationStateContext,
  );

  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [snoozeModalOpen, setSnoozeModalOpen] = useState(false);
  const [markSpamModalOpen, setMarkSpamModalOpen] = useState(false);
  const [unmarkSpamModalOpen, setUnmarkSpamModalOpen] = useState(false);
  const [changeSubjectModalOpen, setChangeSubjectModalOpen] = useState(false);
  const [actionsDropdownAnchor, setActionsDropdownAnchor] =
    useState<HTMLElement | null>(null);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);

  const {
    suggestedConversationsToMerge,
    mergeSuggestionDismissed,
    mergeSuggestionFlowState,
    setMergeSuggestionFlowState,
    resetMergeSuggestionFlow,
  } = useMergeSuggestions(activeConversation);

  const modalOpen =
    mergeModalOpen ||
    closeModalOpen ||
    reopenModalOpen ||
    archiveModalOpen ||
    snoozeModalOpen ||
    markSpamModalOpen ||
    unmarkSpamModalOpen;
  const [leftPanelOpen, setLeftPanelOpen] = useLocalStorage<boolean>(
    "redo.support.conversation-detail.left-panel-open",
    true,
  );
  const [rightPanelOpen, setRightPanelOpen] = useLocalStorage<boolean>(
    "redo.support.conversation-detail.right-panel-open",
    true,
  );

  const handleSetRightPanelOpen = (value: boolean) => {
    setRightPanelOpen(value);
  };

  const [conversationClosing, setConversationClosing] = useState(false);
  const cardListScrollAreaRef = useRef<HTMLDivElement>(null);
  const conversationDetailRef = useRef<HTMLDivElement>(null);

  const canCloseTicket = useHasPermission(
    SupportPermissions.CLOSE_CONVERSATION,
  );
  const canCreateReply = useHasPermission(SupportPermissions.CREATE_REPLY);
  const canArchiveTicket = useHasPermission(
    SupportPermissions.ARCHIVE_CONVERSATION,
  );

  const [pendingNextConversation, setPendingNextConversation] =
    useState<boolean>(false);

  const [conversationsInProximity, setConversationsInProximity] = useState<
    MinimalConversation[]
  >([]); //todo: this list is not really unnecessary and should be replaced with the main filtered list.

  const { nextConversationInList, prevConversationInList } = useMemo(() => {
    if (!activeConversation || !conversationsInProximity?.length) {
      return {
        nextConversationInList: undefined,
        prevConversationInList: undefined,
      };
    }

    const indexOfCurrentConversation = conversationsInProximity.findIndex(
      (conversation) => conversation._id === activeConversation._id,
    );

    if (indexOfCurrentConversation === -1) {
      return {
        nextConversationInList: undefined,
        prevConversationInList: undefined,
      };
    }

    return {
      nextConversationInList:
        conversationsInProximity[indexOfCurrentConversation + 1],
      prevConversationInList:
        conversationsInProximity[indexOfCurrentConversation - 1],
    };
  }, [activeConversation, conversationsInProximity]);

  const { validationResult, revalidate, isValidating } =
    useConversationCloseValidation(activeConversation);

  // Pass in the new active conversation to prevent race conditions in the setting of state.
  const reloadNearbyConversations = async (
    newActiveConversation: MinimalConversation | undefined,
  ) => {
    if (!newActiveConversation) {
      return;
    }
    setPendingNextConversation(true);
    const currentActiveConversation = newActiveConversation;
    const reversedSortFilter = {
      ...finalizedFilters,
      sort: {
        direction:
          finalizedFilters.sort?.direction === SortDirection.ASC
            ? SortDirection.DESC
            : SortDirection.ASC,
        key: finalizedFilters.sort?.key ?? "lastResponse",
      },
    };

    const [prevConversations, nextConversations] = await Promise.all([
      getMinimalConversations(client, {
        pageContinue: currentActiveConversation.lastResponseAt,
        pageStop: undefined,
        pageSize: 10,
        filters: reversedSortFilter,
      }),
      getMinimalConversations(client, {
        pageContinue: currentActiveConversation.lastResponseAt,
        pageStop: undefined,
        pageSize: 10,
        filters: finalizedFilters,
      }),
    ]);

    // Reverse prevConversations to order the whole group
    const reversePrev = prevConversations.data.reverse();
    setConversationsInProximity([
      ...reversePrev.filter(
        (conversation) => conversation._id !== currentActiveConversation._id,
      ),
      currentActiveConversation,
      ...nextConversations.data.filter(
        (conversation) => conversation._id !== currentActiveConversation._id,
      ),
    ]);
    setPendingNextConversation(false);
  };

  const removeConversationFromProximity = useHandler(
    (conversationToExclude: MinimalConversation) => {
      const conversationsInProximityWithoutRemoved =
        conversationsInProximity?.filter(
          (conv) => conv._id !== conversationToExclude!._id,
        );
      setConversationsInProximity(conversationsInProximityWithoutRemoved);
    },
  );

  useEffect(() => {
    // Load nearby conversations if not already loaded or if current conversation is not in the list
    if (!activeConversation) {
      return;
    }
    if (
      !conversationsInProximity?.length ||
      !conversationsInProximity.some(
        (conversation) => conversation._id === activeConversation._id,
      )
    ) {
      sinkPromise(reloadNearbyConversations(activeConversation));
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?._id, conversationsInProximity?.length]);

  const onConversationsViewed = (conversation: MinimalConversation) => {
    if (!team) {
      return;
    }
    void markConversationAsRead(client, conversation, team, user._id);
    resetMergeSuggestionFlow();
    return conversation;
  };

  const handleCardClick = useHandler<CardClickHandler<MinimalConversation>>(
    // To keep conversation switching fast, don't await any network requests in this function.
    (record) => {
      const newRecord = onConversationsViewed(record);
      amplitude.logEvent("view-conversationFromCardList", {
        mode: "single",
        conversationId: newRecord?._id,
        teamId: team._id,
        channels: [newRecord?.platform],
      });
      setActiveConversation(newRecord);
    },
  );

  const isConversationActive = (conversation: MinimalConversation) => {
    return activeConversation?._id === conversation._id;
  };

  const handleMarkedUnread = async (
    conversationToMark: MinimalConversation | undefined,
  ) => {
    if (conversationToMark && team) {
      await markConversationAsUnread(
        client,
        conversationToMark,
        team,
        user._id,
      );
      const searchParams = new URLSearchParams(location.search);
      const ACTIVE_CONVERSATION_QUERY_PARAMETER = "activeConversationId";
      searchParams.delete(ACTIVE_CONVERSATION_QUERY_PARAMETER);
      navigate(`${location.pathname}?${searchParams}`);
      setActiveConversation(undefined);
    }
  };

  const handleMarkInProgress = async (conversation: MinimalConversation) => {
    const updatedConversation = await updateConversation(client, conversation, {
      status: "in_progress",
    });
    setActiveConversation(updatedConversation.data);
  };

  const onKeyDown = async (e: KeyboardEvent) => {
    if (modalOpen || conversationClosing || pendingNextConversation) return;
    if (e.altKey) {
      switch (e.code) {
        case "KeyC": // Alt+C to close ticket
          e.preventDefault();
          await actions.doCloseTicketAction();
          break;

        case "KeyR": // Alt+R to reopen ticket
          e.preventDefault();
          actions.doReopenTicketAction();
          break;

        case "KeyS": // Alt+S to snooze ticket
          e.preventDefault();
          actions.doSnoozeTicketAction();
          break;

        case "KeyP": // Alt+P to mark in progress
          e.preventDefault();
          await actions.doMarkInProgressAction();
          break;

        case "ArrowUp": // Alt+Up to go to previous conversation
          e.preventDefault();
          if (prevConversationInList) {
            setActiveConversation(prevConversationInList);
          }
          break;

        case "ArrowDown": // Alt+Down to go to next conversation
          if (nextConversationInList) {
            setActiveConversation(nextConversationInList);
          }
          e.preventDefault();
          break;
      }
    }
  };

  const actionCleanupFn = () => {
    if (reopenModalOpen) {
      setFilters({ ...filters, status: FiltersStatus.OPEN });
    }
  };

  const actions = {
    doCloseTicketAction: async () => {
      if (
        !canCloseTicket ||
        activeConversation?.status ===
          ConversationStatus.CLOSED.toLowerCase() ||
        !team
      )
        return;

      if (!validationResult?.canClose) {
        const tagGroupNames = validationResult?.missingTagGroups
          .map((group) => group.name)
          .join(", ");
        toast(
          `This conversation cannot be closed because it's missing required tags from the following groups: ${tagGroupNames}. Please add tags from these groups before closing.`,
          { variant: "error" },
        );
        return;
      }

      if (
        localStorage.getItem("redo.doNotShowCloseTicketModal") === "true" ||
        activeConversation?.platform !== ConversationPlatform.REDO_CHAT
      ) {
        setConversationClosing(true);

        await handleCloseTickets({
          client,
          conversations: [activeConversation!],
          setActiveConversation,
          team,
          inDetailView: true,
          removeConversation: (conversation: MinimalConversation) => {
            //keep cached proximity list in sync with filtered list
            removeConversation(conversation);
            removeConversationFromProximity(conversation);
          },
          nextConversationInList,
          prevConversationInList,
        });
        setConversationClosing(false);
      } else {
        amplitude.logEvent("view-closeConversationModal", {
          mode: "single",
          conversationIds: [activeConversation?._id],
          channels: [activeConversation?.platform],
        });
        setCloseModalOpen(true);
      }
    },
    doReopenTicketAction: () => {
      if (
        !canCloseTicket ||
        activeConversation?.status === ConversationStatus.OPEN.toLowerCase()
      )
        return;
      amplitude.logEvent("view-reopenConversationModal", {
        mode: "single",
        conversationIds: [activeConversation?._id],
        channels: [activeConversation?.platform],
      });
      setReopenModalOpen(true);
    },
    doSnoozeTicketAction: () => {
      if (!canCreateReply) return;
      amplitude.logEvent("view-snoozeConversationModal", {
        mode: "single",
        conversationIds: [activeConversation?._id],
        channels: [activeConversation?.platform],
      });
      setSnoozeModalOpen(true);
    },
    doUnsnoozeTicketAction: async () => {
      amplitude.logEvent("single-unsnoozeConversation", {
        mode: "single",
        conversationIds: [activeConversation?._id],
        channels: [activeConversation?.platform],
      });
      const updatedConversation = await updateConversation(
        client,
        activeConversation,
        { snoozedUntil: null },
      );
      setActiveConversation(updatedConversation.data);
    },
    doMarkInProgressAction: async () => {
      if (!canCloseTicket) return;
      amplitude.logEvent("markInProgress", {
        mode: "single",
        conversationIds: [activeConversation?._id],
        channels: [activeConversation?.platform],
      });
      setConversationsInProximity((prev: MinimalConversation[]) =>
        prev.filter((conv) => activeConversation._id !== conv._id),
      );
      await handleMarkInProgress(activeConversation);
      setFilters({ ...filters, status: FiltersStatus.IN_PROGRESS });
    },
    doArchiveTicketAction: () => {
      if (!canArchiveTicket) return;
      amplitude.logEvent("view-archiveConversationModal", {
        mode: "single",
        conversationIds: [activeConversation?._id],
        channels: [activeConversation?.platform],
      });
      setArchiveModalOpen(true);
    },
    doMarkUnreadTicketAction: async () => {
      if (
        !activeConversation?.messages?.some(
          (message) => message.type === "customer",
        )
      )
        return;
      if (
        advancedFilters.some(
          (advancedFilter) =>
            advancedFilter.type === FilterGroupFilterOption.READ,
        )
      ) {
        removeConversationFromProximity(activeConversation!);
      }
      await handleMarkedUnread(activeConversation);
    },
    doMergeTicketAction: () => {
      if (!canCloseTicket) return;
      amplitude.logEvent("view-mergeConversationModal", {
        mode: "single",
        conversationIds: [activeConversation?._id],
        channels: [activeConversation?.platform],
      });
      setMergeModalOpen(true);
    },
    doMarkSpamTicketAction: () => {
      if (
        activeConversation?.platform !== ConversationPlatform.EMAIL ||
        !canCloseTicket
      )
        return;
      amplitude.logEvent("view-markSpamModal", {
        mode: "single",
        conversationId: activeConversation?._id,
      });
      setMarkSpamModalOpen(true);
    },
    doUnmarkSpamTicketAction: () => {
      if (
        activeConversation?.platform !== ConversationPlatform.EMAIL ||
        !canCloseTicket
      )
        return;
      amplitude.logEvent("view-unmarkSpamModal", {
        mode: "single",
        conversationId: activeConversation?._id,
      });
      setUnmarkSpamModalOpen(true);
    },
    doChangeSubjectAction: () => {
      if (
        activeConversation?.platform !== ConversationPlatform.EMAIL ||
        !canCreateReply
      )
        return;
      amplitude.logEvent("view-changeSubjectModal", {
        mode: "single",
        conversationId: activeConversation?._id,
      });
      setChangeSubjectModalOpen(true);
    },
  };

  const isMac = browserIsMac();
  const isSnoozedConversation =
    getConversationStatus(activeConversation) === ConversationStatus.SNOOZED;

  const actionButtons = [
    {
      show: canCloseTicket,
      buttonComponent:
        activeConversation?.status !==
        ConversationStatus.CLOSED.toLowerCase() ? (
          <Tooltip arrow key="close" title={isMac ? "Option+C" : "Alt+C"}>
            <span>
              <Button
                className={conversationHeaderCss.headerButtons}
                disabled={pendingNextConversation}
                onClick={actions.doCloseTicketAction}
                size={ButtonSize.NANO}
                theme={ButtonTheme.PRIMARY}
              >
                <Flex align="center" gap="xs" justify="center" wrap="nowrap">
                  <Flex
                    align="center"
                    className={classNames(
                      conversationHeaderCss.buttonIconWrapper,
                      conversationHeaderCss.closeButton,
                    )}
                    justify="center"
                  >
                    <CheckSvg />
                  </Flex>
                  <Text fontSize="xs" fontWeight="medium">
                    Close
                  </Text>
                </Flex>
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip arrow key="reopen" title={isMac ? "Option+R" : "Alt+R"}>
            <span>
              <Button
                className={conversationHeaderCss.headerButtons}
                onClick={actions.doReopenTicketAction}
                size={ButtonSize.NANO}
                theme={ButtonTheme.PRIMARY}
              >
                <div className={singleConversationViewCss.buttonContent}>
                  <UndoArrow className={singleConversationViewCss.icon} />
                  <Text fontSize="xs" fontWeight="medium">
                    Reopen
                  </Text>
                </div>
              </Button>
            </span>
          </Tooltip>
        ),
    },
    {
      show:
        canCloseTicket &&
        activeConversation?.status ===
          ConversationStatus.IN_PROGRESS.toLowerCase(),
      commandMenuItem: {
        Icon: UndoArrow,
        text: "Mark as open",
        onClick: actions.doReopenTicketAction,
        tooltip: isMac ? "Option+R" : "Alt+R",
      },
    },
    {
      show: canCreateReply && !isSnoozedConversation,
      commandMenuItem: {
        Icon: SnoozeClockIcon,
        text: "Snooze",
        onClick: actions.doSnoozeTicketAction,
        tooltip: isMac ? "Option+S" : "Alt+S",
      },
    },
    {
      show: canCreateReply && isSnoozedConversation,
      commandMenuItem: {
        Icon: ClockRewindIcon,
        text: "Unsnooze",
        onClick: actions.doUnsnoozeTicketAction,
        tooltip: isMac ? "Option+S" : "Alt+S",
      },
    },
    {
      show:
        canCloseTicket &&
        team.settings.support?.useInProgressStatus &&
        activeConversation?.status !==
          ConversationStatus.IN_PROGRESS.toLowerCase(),
      commandMenuItem: {
        Icon: StatusSvg,
        text: "Mark in progress",
        onClick: actions.doMarkInProgressAction,
        tooltip: isMac ? "Option+P" : "Alt+P",
      },
    },
    {
      show: canArchiveTicket,
      commandMenuItem: {
        Icon: ArchiveIcon,
        text: "Delete",
        onClick: actions.doArchiveTicketAction,
      },
    },
    {
      show:
        activeConversation?.messages?.length &&
        activeConversation?.messages?.some(
          (message) => message.type === "customer",
        ),
      commandMenuItem: {
        Icon: EnvelopeIcon,
        text: "Mark unread",
        onClick: actions.doMarkUnreadTicketAction,
      },
    },
    {
      show:
        canCloseTicket &&
        activeConversation?.platform &&
        ![
          ConversationPlatform.INSTAGRAM_COMMENTS,
          ConversationPlatform.FACEBOOK_COMMENTS,
        ].includes(activeConversation.platform as ConversationPlatform),
      commandMenuItem: {
        Icon: MergeIcon,
        text: "Merge",
        onClick: actions.doMergeTicketAction,
      },
    },
    {
      show:
        activeConversation?.platform === ConversationPlatform.EMAIL &&
        activeConversation.tagIds?.every((tag) => tag.name !== SPAM_TAG) &&
        canCloseTicket,
      commandMenuItem: {
        Icon: SpamIcon,
        text: "Mark spam",
        onClick: actions.doMarkSpamTicketAction,
      },
    },
    {
      show:
        activeConversation?.platform === ConversationPlatform.EMAIL &&
        activeConversation.tagIds?.some((tag) => tag.name === SPAM_TAG) &&
        canCloseTicket,
      commandMenuItem: {
        Icon: AnnotationCheck,
        text: "Unmark spam",
        onClick: actions.doUnmarkSpamTicketAction,
      },
    },
    {
      show:
        activeConversation?.platform === ConversationPlatform.EMAIL &&
        canCreateReply,
      commandMenuItem: {
        Icon: Edit04Icon,
        text: "Change subject",
        onClick: actions.doChangeSubjectAction,
      },
    },
  ];

  const enabledActionButtons = actionButtons.filter(
    (actionButton) => actionButton.show,
  );

  const commandMenuItems = enabledActionButtons
    .slice(1)
    .map((actionButton) => actionButton.commandMenuItem)
    .filter((item): item is NonNullable<typeof item> => item !== undefined);

  const [conversationJustMerged, setConversationJustMerged] = useState(false);

  const conversationActionButtons = (
    <div className={singleConversationViewCss.portalButtonsWrapper}>
      {enabledActionButtons.length > 1 && (
        <div className={conversationHeaderCss.threeDotsButton}>
          <Button
            disabled={pendingNextConversation}
            onClick={() => setActionsDropdownOpen(!actionsDropdownOpen)}
            ref={setActionsDropdownAnchor}
            size={ButtonSize.NANO}
            theme={ButtonTheme.OUTLINED}
          >
            <Flex
              align="center"
              className={conversationHeaderCss.buttonIconWrapper}
              justify="center"
            >
              <DotsHorizontalSvg />
            </Flex>
          </Button>
          <RedoCommandMenu
            anchor={actionsDropdownAnchor}
            items={commandMenuItems}
            open={actionsDropdownOpen}
            setOpen={setActionsDropdownOpen}
          />
        </div>
      )}
      {enabledActionButtons
        .slice(0, 1)
        .map((actionButton) => actionButton.buttonComponent)}
      {mergeModalOpen && (
        <MergeModal
          cleanupFn={() => {
            setConversationJustMerged(true);
            actionCleanupFn();
          }}
          conversations={[activeConversation]}
          open={mergeModalOpen}
          setActiveConversation={setActiveConversation}
          setConversationsInProximity={setConversationsInProximity}
          setOpen={setMergeModalOpen}
          suggestedConversations={suggestedConversationsToMerge ?? []}
        />
      )}
      {closeModalOpen && (
        <CloseTicketModal
          cleanupFn={actionCleanupFn}
          conversations={[activeConversation]}
          inDetailView
          nextConversationInList={nextConversationInList}
          open={closeModalOpen}
          prevConversationInList={prevConversationInList}
          setActiveConversation={setActiveConversation}
          setOpen={setCloseModalOpen}
        />
      )}
      {reopenModalOpen && (
        <ReopenTicketModal
          cleanupFn={() => {
            actionCleanupFn();
          }}
          conversations={[activeConversation]}
          inDetailView
          open={reopenModalOpen}
          setActiveConversation={setActiveConversation}
          setOpen={setReopenModalOpen}
        />
      )}
      {archiveModalOpen && (
        <ArchiveTicketModal
          cleanupFn={actionCleanupFn}
          conversations={[activeConversation]}
          inDetailView
          nextConversationInList={nextConversationInList}
          open={archiveModalOpen}
          prevConversationInList={prevConversationInList}
          setActiveConversation={setActiveConversation}
          setConversationsInProximity={setConversationsInProximity}
          setOpen={setArchiveModalOpen}
        />
      )}
      {snoozeModalOpen && (
        <SnoozeModal
          cleanupFn={actionCleanupFn}
          conversations={[activeConversation]}
          inDetailView
          nextConversationInList={nextConversationInList}
          open={snoozeModalOpen}
          prevConversationInList={prevConversationInList}
          setActiveConversation={setActiveConversation}
          setConversationsInProximity={setConversationsInProximity}
          setOpen={setSnoozeModalOpen}
        />
      )}
      {markSpamModalOpen && (
        <MarkSpamModal
          cleanupFn={actionCleanupFn}
          conversations={[activeConversation]}
          inDetailView
          nextConversationInList={nextConversationInList}
          open={markSpamModalOpen}
          prevConversationInList={prevConversationInList}
          setActiveConversation={setActiveConversation}
          setConversationsInProximity={setConversationsInProximity}
          setOpen={setMarkSpamModalOpen}
        />
      )}
      {unmarkSpamModalOpen && (
        <UnmarkSpamModal
          cleanupFn={actionCleanupFn}
          conversations={[activeConversation]}
          inDetailView
          nextConversationInList={nextConversationInList}
          open={unmarkSpamModalOpen}
          prevConversationInList={prevConversationInList}
          setActiveConversation={setActiveConversation}
          setConversationsInProximity={setConversationsInProximity}
          setOpen={setUnmarkSpamModalOpen}
        />
      )}
      {changeSubjectModalOpen && (
        <ChangeSubjectModal
          conversation={activeConversation}
          resolve={() => setChangeSubjectModalOpen(false)}
        />
      )}
    </div>
  );

  const sortDefault: TableSort = {
    direction: SortDirection.DESC,
    key: "lastResponse",
  };

  const [refreshTableButtonPending, setRefreshTableButtonPending] =
    useState(false);

  function handleRefreshTableButton() {
    sinkPromise(
      (async () => {
        if (refreshTableButtonPending) {
          return;
        }

        const failToast = () => {
          toast("Failed to refresh conversations", { variant: "error" });
        };
        setRefreshTableButtonPending(true);

        await waitForRefresh().catch(failToast);
        setRefreshTableButtonPending(false);
      })(),
    );
  }

  const shouldAllowClickingCardListRefresh = !blockRefresh;

  return (
    <ActiveConversationContext.Provider
      value={{
        div: conversationDetailRef,
        ticketActions: actions,
        conversationClosing,
        setConversationClosing,
        setActiveConversation,
        activeConversation,
      }}
    >
      <div
        className={classNames(singleConversationViewCss.wide)}
        onKeyDown={onKeyDown}
        ref={conversationDetailRef}
        tabIndex={0}
      >
        <Flex dir="column" gap="none" w="full">
          <Flex gap="none" grow={1}>
            <div
              className={singleConversationViewCss.panel}
              style={!leftPanelOpen ? { display: "none" } : undefined}
            >
              <div
                className={classNames(
                  singleConversationViewCss.tableSummaryContainer,
                  singleConversationViewCss.fullHeight,
                )}
              >
                <div className={singleConversationViewCss.tableSummaryHeader}>
                  <Flex
                    align="center"
                    grow={1}
                    justify="space-between"
                    pl="xl"
                    pr="xl"
                  >
                    <Flex>
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        textColor="primary"
                      >
                        Tickets
                      </Text>
                    </Flex>
                    <Flex>
                      <RedoButton
                        disabled={!shouldAllowClickingCardListRefresh}
                        hierarchy="secondary"
                        IconLeading={() => <RefreshCCW01Svg />}
                        onClick={handleRefreshTableButton}
                        pending={refreshTableButtonPending}
                      />
                    </Flex>
                  </Flex>
                </div>
                <div
                  className={singleConversationViewCss.cardList}
                  ref={cardListScrollAreaRef}
                >
                  <CardList
                    card={ConversationInfoCardRender}
                    fetcher={fetcher}
                    inactive={!leftPanelOpen}
                    isCardActive={isConversationActive}
                    onCardClick={handleCardClick}
                    passThroughValues={finalizedFilters}
                    ref={cardListRef}
                    scrollAreaRef={cardListScrollAreaRef}
                    sortDefault={sortDefault}
                  />
                </div>
              </div>
            </div>
            <ConversationCloseValidationContext.Provider
              value={{ revalidate, validationResult, isValidating }}
            >
              <div className={singleConversationViewCss.centerContent}>
                <div
                  className={classNames(
                    singleConversationViewCss.conversationDetailContainer,
                    singleConversationViewCss.fullHeight,
                  )}
                >
                  <ConversationSendStateContextProvider>
                    <ConversationContent
                      actionButtons={conversationActionButtons}
                      conversation={activeConversation}
                      conversationJustMerged={conversationJustMerged}
                      fullConversation={fullConversation}
                      fullConversationLoading={fullConversationLoading}
                      handleSetLeftPanelOpen={setLeftPanelOpen}
                      handleSetRightPanelOpen={handleSetRightPanelOpen}
                      leftPanelOpen={leftPanelOpen}
                      nextConversationInList={nextConversationInList}
                      onConversationsViewed={onConversationsViewed}
                      pendingNextConversation={pendingNextConversation}
                      prevConversationInList={prevConversationInList}
                      removeConversationFromProximity={
                        removeConversationFromProximity
                      }
                      rightPanelOpen={rightPanelOpen}
                      setActiveConversation={setActiveConversation}
                    />
                  </ConversationSendStateContextProvider>
                </div>
                <div
                  className={
                    rightPanelOpen
                      ? classNames(
                          singleConversationViewCss.panel,
                          singleConversationViewCss.right,
                        )
                      : singleConversationViewCss.hidden
                  }
                >
                  <div
                    className={
                      singleConversationViewCss.conversationContextContainer
                    }
                  >
                    <ConversationContext
                      conversation={fullConversation ?? activeConversation}
                      setActiveConversation={setActiveConversation}
                      setConversationAssignee={(user: User | null) => {
                        setActiveConversation({
                          ...activeConversation,
                          assignee: user,
                        });
                        if (fullConversation) {
                          setFullConversation({
                            ...fullConversation,
                            assignee: user,
                          });
                        }
                      }}
                    />
                    <div>
                      {mergeSuggestionFlowState &&
                        suggestedConversationsToMerge && (
                          <MergeSuggestionFlow
                            activeConversation={activeConversation}
                            alreadyDismissed={mergeSuggestionDismissed}
                            initialStep={mergeSuggestionFlowState}
                            onClose={() =>
                              setMergeSuggestionFlowState(undefined)
                            }
                            suggestedConversations={
                              suggestedConversationsToMerge
                            }
                          />
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </ConversationCloseValidationContext.Provider>
          </Flex>
        </Flex>
      </div>
    </ActiveConversationContext.Provider>
  );
});
